import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import * as Icons from 'lucide-react';

const PermissionMatrix = ({ permissions }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedModules, setExpandedModules] = useState({});

  // Toggle module expansion
  const toggleModule = (moduleKey) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  // Expand all modules
  const expandAll = () => {
    const allExpanded = {};
    permissions.modules?.forEach(module => {
      allExpanded[module.key] = true;
    });
    setExpandedModules(allExpanded);
  };

  // Collapse all modules
  const collapseAll = () => {
    setExpandedModules({});
  };

  // Filter modules based on search
  const filteredModules = permissions.modules?.filter(module => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const moduleMatches = module.label.toLowerCase().includes(searchLower) ||
                          module.key.toLowerCase().includes(searchLower);
    
    const permissionMatches = module.permissions?.some(perm => 
      perm.action.toLowerCase().includes(searchLower) ||
      perm.description.toLowerCase().includes(searchLower)
    );
    
    return moduleMatches || permissionMatches;
  });

  // Get icon component
  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <Icons.Box className="h-5 w-5" />;
  };

  // Action color mapping
  const actionColors = {
    read: 'bg-blue-100 text-blue-700 border-blue-200',
    create: 'bg-green-100 text-green-700 border-green-200',
    update: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    delete: 'bg-red-100 text-red-700 border-red-200',
    export: 'bg-purple-100 text-purple-700 border-purple-200',
    import: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  };

  return (
    <div className="space-y-4">
      {/* Search and controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Rechercher un module ou une permission..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={expandAll}
                className="text-sm text-blue-600 hover:text-blue-700 px-3 py-1 rounded hover:bg-blue-50"
              >
                Tout développer
              </button>
              <button
                onClick={collapseAll}
                className="text-sm text-gray-600 hover:text-gray-700 px-3 py-1 rounded hover:bg-gray-100"
              >
                Tout réduire
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modules list */}
      <div className="space-y-3">
        {filteredModules?.map((module) => {
          const isExpanded = expandedModules[module.key];
          
          return (
            <Card key={module.key} className="overflow-hidden">
              <CardHeader
                className="cursor-pointer hover:bg-gray-50 transition-colors pb-3"
                onClick={() => toggleModule(module.key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                    <div className="text-emerald-600">
                      {getIcon(module.icon)}
                    </div>
                    <div>
                      <CardTitle className="text-base">{module.label}</CardTitle>
                      <CardDescription className="text-xs">
                        {module.permissions_count} permission{module.permissions_count > 1 ? 's' : ''}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant="outline" className="font-mono text-xs">
                    {module.key}
                  </Badge>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="pt-0">
                  <div className="border-t pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {module.permissions?.map((permission) => {
                        const actionColor = actionColors[permission.action] || 'bg-gray-100 text-gray-700 border-gray-200';
                        
                        return (
                          <div
                            key={permission.key}
                            className="flex items-start gap-3 p-3 rounded-lg border bg-gray-50 hover:bg-white transition-colors"
                          >
                            <Badge
                              variant="outline"
                              className={`${actionColor} text-xs font-medium shrink-0`}
                            >
                              {permission.action}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                {permission.description}
                              </p>
                              <p className="text-xs text-gray-500 font-mono truncate">
                                {permission.key}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Empty state */}
      {filteredModules?.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 mb-2">Aucune permission trouvée</p>
            <p className="text-sm text-gray-500">
              Essayez un autre terme de recherche
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PermissionMatrix;
